"use client"

import { useEffect } from "react"
import type { RefObject } from "react"
import type { BooruPost } from "@/lib/booru-api"

// Moved out of components/booru-detail-modal.tsx: two developer-only logging effects (same code, same dependencies).
// They do nothing unless localStorage "booruAlbumDebug" / "booruLayoutDebug" is "1".
export function useModalDebugLogging({
  open,
  post,
  isAlbum,
  albumPages,
  activeVideoUrl,
  isVideo,
  isCurrentAlbumPageVideo,
  isVideoLoading,
  mediaRootRef,
  topToolbarRef,
  videoRef,
}: {
  open: boolean
  post: BooruPost | null
  isAlbum: boolean
  albumPages: string[]
  activeVideoUrl: string | null
  isVideo: boolean
  isCurrentAlbumPageVideo: boolean
  isVideoLoading: boolean
  mediaRootRef: RefObject<HTMLDivElement | null>
  topToolbarRef: RefObject<HTMLDivElement | null>
  videoRef: RefObject<HTMLVideoElement | null>
}) {
  useEffect(() => {
    if (!isAlbum) return
    if (post?.site !== "erome") return

    let debugEnabled = false
    try {
      debugEnabled = localStorage.getItem("booruAlbumDebug") === "1"
    } catch {}
    if (!debugEnabled) return

    const uniqueUrls = Array.from(new Set(albumPages))
    console.log("[booru] Album media debug", {
      postId: post?.id,
      site: post?.site,
      totalPages: albumPages.length,
      uniqueUrls: uniqueUrls.length,
      pages: albumPages,
    })
  }, [albumPages, isAlbum, post?.id, post?.site])

  useEffect(() => {
    if (!open) return
    if (post?.site !== "erome") return

    let debugEnabled = false
    try {
      debugEnabled = localStorage.getItem("booruLayoutDebug") === "1"
    } catch {}
    if (!debugEnabled) return

    const dialogEl = document.querySelector('[data-slot="dialog-content"]') as HTMLElement | null
    const closeEl = document.querySelector('[data-slot="dialog-close"]') as HTMLElement | null
    const mediaEl = mediaRootRef.current
    const toolbarEl = topToolbarRef.current
    const videoEl = videoRef.current

    const toRect = (el: Element | null) => {
      if (!el) return null
      const r = el.getBoundingClientRect()
      return { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) }
    }

    const truncateUrl = (url: string | null | undefined) => {
      if (!url) return url
      if (url.length <= 90) return url
      return `${url.slice(0, 70)}…${url.slice(-16)}`
    }

    let lastKey = ""
    const logSnapshot = (reason: string) => {
      const dialogRect = toRect(dialogEl)
      const closeRect = toRect(closeEl)
      const mediaRect = toRect(mediaEl)
      const toolbarRect = toRect(toolbarEl)
      const videoRect = toRect(videoEl)
      const videoState = videoEl
        ? {
            readyState: videoEl.readyState,
            networkState: videoEl.networkState,
            videoWidth: videoEl.videoWidth,
            videoHeight: videoEl.videoHeight,
            paused: videoEl.paused,
            currentTime: Math.round(videoEl.currentTime * 1000) / 1000,
            src: truncateUrl(videoEl.currentSrc || videoEl.src),
          }
        : null

      const key = JSON.stringify({
        dialogRect,
        closeRect,
        mediaRect,
        toolbarRect,
        videoRect,
        videoState,
        activeVideoUrl,
        isVideoLoading,
        isAlbum,
        isVideo,
        isCurrentAlbumPageVideo,
      })
      if (key === lastKey) return
      lastKey = key

      console.log("[booru-detail-modal layout]", reason, {
        site: post.site,
        postId: post.id,
        viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
        activeVideoUrl: truncateUrl(activeVideoUrl),
        isVideoLoading,
        isAlbum,
        isVideo,
        isCurrentAlbumPageVideo: !!isCurrentAlbumPageVideo,
        dialogRect,
        closeRect,
        toolbarRect,
        mediaRect,
        videoRect,
        videoState,
      })
    }

    logSnapshot("open")

    const observed = [dialogEl, closeEl, mediaEl, toolbarEl, videoEl].filter(Boolean) as Element[]
    const ro = new ResizeObserver(() => logSnapshot("resize"))
    observed.forEach((el) => ro.observe(el))

    const handleWindowResize = () => logSnapshot("window-resize")
    window.addEventListener("resize", handleWindowResize)

    return () => {
      window.removeEventListener("resize", handleWindowResize)
      ro.disconnect()
    }
  }, [activeVideoUrl, isAlbum, isCurrentAlbumPageVideo, isVideo, isVideoLoading, open, post?.id, post?.site])
}
